import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { PREPARING_DOWNLOAD, RemoteShellWebComponentSchema } from '../types';
import { TemplateState } from '../../../types/common/types';
import { NirvanaErrorCode } from '@nirvana/shared/dtos/types/shared/communication';
import { translateFn } from '../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { DownloadWindowWebComponent } from '../../downloadWindow';
import {
  START_VALIDATE_DOWNLOAD_PATH,
  VALIDATE_ARCHIVE_PASSWORD,
  VALIDATE_DOWNLOAD_PATH,
  VALIDATE_PASSWORD_TOGGLE,
  VALIDATE_REPEAT_PASSWORD,
} from '../../downloadWindow/types';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';

export class RemoteShellView extends BaseWebComponentView {
  protected downloadDialog: GzDialogWebComponent | null = null;
  public dialog: GzDialogWebComponent | null = null;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom.$remoteShellTitle = this.shadow.querySelector('#remote-shell-title');
    this.dom.$endpointDetails = this.shadow.querySelector('#endpoint-details');
    this.dom.$terminalContainer = this.shadow.querySelector('#terminal-container');

    this.dom.$endSessionButton = this.shadow.querySelector('#end-session-button');
    this.dom.$leftCardButtonsBox = this.shadow.querySelector('.left-card-footer-buttons');

    this.dom.$connectionDetails = this.shadow.querySelector('#connection-details');
    this.dom.$connectionDetailsInfo = this.shadow.querySelector('#remote-shell-connection-info');

    this.dom.$errorMessage = this.shadow.querySelector('#error-message');

    this.dom.$loadingOverlay = this.shadow.querySelector('#loading-overlay');

    this.dom.$uploadButton = this.shadow.querySelector('#upload-button');
    this.dom.$uploadButtonTooltip = this.shadow.querySelector('#upload-button-tooltip');

    this.dom.$downloadButton = this.shadow.querySelector('#download-button');
    this.dom.$downloadButtonTooltip = this.shadow.querySelector('#download-button-tooltip');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setTranslatedStrings() {
    this.dom.$errorMessage.innerText = this.commonStrings.generalSystemError2;
    this.dom.$endSessionButton.textContent = this.translations.endSessionButtonText;
    this.dom.$remoteShellTitle.innerText = this.translations.remoteShellTitle;
    this.dom.$uploadButton.textContent = this.translations.upload.button;
    this.dom.$downloadButton.textContent = this.translations.download.button;
    this.dom.$connectionDetailsInfo.innerText = this.translations.remoteShellConnectionInfo;
  }

  public getTranslations() {
    this.translations = this.stringsTranslator.getRemoteShellTranslations();

    return this.translations;
  }

  public translateNirvanaErrorCodes(errorCode: NirvanaErrorCode) {
    return this.stringsTranslator.translateNirvanaErrorCode(errorCode);
  }

  public buildSuccessView(data: RemoteShellWebComponentSchema) {
    window.onbeforeunload = null; // remove popup on tab closing.

    if (data.hasError) {
      this.buildErrorView();
      return;
    }

    this.dom.$endpointDetails.injectDependencies(this.dependencies);
    this.dom.$endpointDetails.endpointId = this.dependencies.endpointId;
    this.dom.$endpointDetails.start().then().catch(console.error);

    this.dom.$terminalContainer.injectDependencies(this.dependencies);
    this.dom.$terminalContainer.start().then().catch(console.error);
  }

  public buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  /**
   *
   * @param {string} path
   * @param {string} streamId
   * @param {number} maxFilesSize
   * @returns {Promise<void>}
   */
  public async handleDownloadButtonClick(path: string, streamId: string | null, maxFilesSize: number) {
    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="confirm" class="mr-2" disabled>${translateFn('Download')}</gz-button>
        <gz-button id="cancel" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
      `;

    const downloadComponent = this.initializeDownloadComponent(
      path,
      dialogFooter.querySelector('#confirm') as GzButtonWebComponent,
      streamId,
      maxFilesSize
    );

    this.initializeDownloadDialog(dialogFooter, downloadComponent);

    downloadComponent.injectDependencies(this.dependencies);
    await downloadComponent.start();
    await downloadComponent.validate();
  }

  initializeDownloadComponent(path, dialogConfirmButton, streamId, maxFilesSize: number) {
    const downloadComponent = new DownloadWindowWebComponent();

    downloadComponent.streamId = streamId;
    downloadComponent.path = path;
    downloadComponent.maxFilesSize = maxFilesSize;

    downloadComponent.addEventListener(START_VALIDATE_DOWNLOAD_PATH, () => {
      dialogConfirmButton.disabled = true;
      dialogConfirmButton.showSpinner = true;
    });

    downloadComponent.addEventListener(VALIDATE_DOWNLOAD_PATH, () => {
      dialogConfirmButton.disabled = !downloadComponent.isValid();
      dialogConfirmButton.showSpinner = false;
    });

    downloadComponent.addEventListener(VALIDATE_PASSWORD_TOGGLE, () => {
      dialogConfirmButton.disabled = !downloadComponent.isValid();
      dialogConfirmButton.showSpinner = false;
    });

    downloadComponent.addEventListener(VALIDATE_ARCHIVE_PASSWORD, () => {
      dialogConfirmButton.disabled = !downloadComponent.isValid();
      dialogConfirmButton.showSpinner = false;
    });

    downloadComponent.addEventListener(VALIDATE_REPEAT_PASSWORD, () => {
      dialogConfirmButton.disabled = !downloadComponent.isValid();
      dialogConfirmButton.showSpinner = false;
    });

    return downloadComponent;
  }

  initializeDownloadDialog(dialogFooter, downloadComponent) {
    const dialogConfig = {
      headline: this.dependencies.translateFn('Download files'),
      bodyContent: downloadComponent,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      footerContent: dialogFooter,
    };

    const footerConfirmBtn = dialogFooter.querySelector('#confirm') as GzButtonWebComponent;
    footerConfirmBtn?.addEventListener('click', async () => {
      if (footerConfirmBtn.showSpinner) {
        return;
      }

      let passwordValue = '';
      if (downloadComponent.isPasswordProtected()) {
        passwordValue = downloadComponent.password ?? '';
      }

      this.dispatchEvent(PREPARING_DOWNLOAD, {
        path: downloadComponent.path,
        streamId: downloadComponent.streamId,
        password: passwordValue,
      });

      this.closeDialog();

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Download started. You will be notified when the download is complete.'),
        severity: ToastSeverity.INFORMATIVE,
      });
    });

    const footerCancelBtn = dialogFooter.querySelector('#cancel');
    footerCancelBtn?.addEventListener('click', () => {
      if (footerConfirmBtn.showSpinner) {
        return;
      }

      this.closeDialog();
    });

    this.dialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.dom.$wrapper);
  }

  dispatchEvent(name, detail = {}) {
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail,
      })
    );
  }

  closeDialog() {
    if (this.dialog) {
      this.dialog.dismiss();
      this.dialog = null;
    }
  }
}
